import { GetInstagramAudioListStatsOrderingKeys, GetInstagramAudioListStatsParams } from '@round/api';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import useUrlState from '../../../../Hooks/useUrlState';
import { useInstagramAudioListStats } from '../hooks/useInstagramAudioListStats';
import styles from './InstagramAudios.module.css';
import InstagramAudioExploreTable from './Table/InstagramAudioExploreTable';
import { useInstagramAudios } from '../hooks/useInstagramAudios';
import { useInstagramUsers } from '../../hooks/useInstagramUsers';
import { GenericDropdownOption } from '../../../../App.types';
import { StylesConfig } from 'react-select';
import Select from '../../../../ui/DataEntry/Select/Select';
import { DebounceInput } from 'react-debounce-input';
import InstagramAudioModal from '../AudioModal/InstagramAudioModal';
import { isNumber } from 'lodash';
import cn from 'classnames';

const orderingOptions: GenericDropdownOption<GetInstagramAudioListStatsOrderingKeys>[] = [
    { label: 'Total Reels', value: '-reels_count' },
    { label: 'Daily Growth (# reels)', value: '-reels_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-reels_count_daily_change_relative' },
    { label: 'Weekly Growth (# reels)', value: '-reels_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-reels_count_weekly_change_relative' },
];

const DEFAULT_ORDERING: GetInstagramAudioListStatsOrderingKeys = '-reels_count_daily_change';

const orderingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

type UrlState = GetInstagramAudioListStatsParams & {
    audio?: number;
};

const initialUrlState: Pick<Required<UrlState>, 'page' | 'page_size' | 'ordering' | 'min_reels_count'> = {
    page: 1,
    page_size: 10,
    ordering: DEFAULT_ORDERING,
    min_reels_count: 100,
};

const InstagramAudios = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);
    const page = urlState.page ? Number(urlState.page) : initialUrlState.page;
    const pageSize = urlState.page_size ? Number(urlState.page_size) : initialUrlState.page_size;
    const titleSearch = urlState.title ?? '';
    const authorSearch = urlState.author ?? '';
    const instagramIdSearch = urlState.instagram_id ?? '';
    const selectedOrderingOption = orderingOptions.find((option) => option.value === urlState.ordering);
    const minReelsCount = urlState.min_reels_count ? Number(urlState.min_reels_count) : undefined;
    const maxReelsCount = urlState.max_reels_count ? Number(urlState.max_reels_count) : undefined;

    const {
        audioStats,
        count,
        isLoading: isStatsLoading,
        isInitialized: isStatsInitialized,
        init: initStats,
        reset: resetStats,
    } = useInstagramAudioListStats({
        page,
        page_size: pageSize,
        ordering: selectedOrderingOption?.value ?? DEFAULT_ORDERING,
        title: titleSearch,
        author: authorSearch,
        instagram_id: instagramIdSearch,
        min_reels_count: minReelsCount,
        max_reels_count: maxReelsCount,
    });

    useAbortableEffect(
        (signal) => {
            if (!isStatsInitialized) {
                initStats({ signal });
            }
        },
        [initStats, isStatsInitialized]
    );

    const audioStatAudioIds = audioStats.map((stat) => stat.audio_id);

    const {
        audios,
        isLoading: isAudiosLoading,
        isInitialized: isAudiosInitialized,
        init: initAudios,
        reset: resetAudios,
    } = useInstagramAudios({
        id: audioStatAudioIds.join(','),
        page_size: audioStatAudioIds.length,
    });

    useAbortableEffect(
        (signal) => {
            if (!isAudiosInitialized && isStatsInitialized) {
                initAudios({ signal });
            }
        },
        [isStatsInitialized, isAudiosInitialized, initAudios]
    );

    const audioUserIds = audios.map((audio) => audio.author).filter(isNumber);

    const {
        users,
        isLoading: isUsersLoading,
        isInitialized: isUsersInitialized,
        init: initUsers,
        reset: resetUsers,
    } = useInstagramUsers({
        id: audioUserIds.join(','),
        page_size: audioUserIds.length,
    });

    useAbortableEffect(
        (signal) => {
            if (!isUsersInitialized && isAudiosInitialized) {
                initUsers({ signal });
            }
        },
        [isAudiosInitialized, isUsersInitialized, initUsers]
    );

    const reset = () => {
        resetStats();
        resetAudios();
        resetUsers();
    };

    const isLoading = isStatsLoading || isAudiosLoading || isUsersLoading;

    const rows = audioStats.map((stat) => {
        const audio = audios.find((audio) => audio.id === stat.audio_id);
        const user = users.find((user) => user.id === audio?.author);
        return {
            ...stat,
            audio: audio ?? null,
            user: user ?? null,
        };
    });

    return (
        <>
            <div className={styles.container}>
                <h1 className={styles.title}>Instagram Audio Search</h1>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={cn(styles.filter, styles.textFilter)}>
                            <label htmlFor="title">Title</label>
                            <DebounceInput
                                id="title"
                                placeholder="Search title"
                                debounceTimeout={500}
                                value={titleSearch}
                                onChange={(e) => {
                                    setUrlState({
                                        title: e.target.value || undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                            />
                        </div>

                        <div className={cn(styles.filter, styles.textFilter)}>
                            <label htmlFor="author">Author</label>
                            <DebounceInput
                                id="author"
                                placeholder="Search author"
                                debounceTimeout={500}
                                value={authorSearch}
                                onChange={(e) => {
                                    setUrlState({
                                        author: e.target.value || undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                            />
                        </div>

                        <div className={cn(styles.filter, styles.textFilter)}>
                            <label htmlFor="instagramId">Reel ID</label>
                            <DebounceInput
                                placeholder="Search Reel ID"
                                id="instagramId"
                                debounceTimeout={500}
                                value={instagramIdSearch}
                                onChange={(e) => {
                                    setUrlState({
                                        instagram_id: e.target.value || undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                            />
                        </div>

                        <div className={styles.filter}>
                            <label htmlFor="minReelsCount">Min reel creations</label>
                            <DebounceInput
                                id="minReelsCount"
                                type="number"
                                debounceTimeout={500}
                                value={minReelsCount}
                                onChange={(e) => {
                                    setUrlState({
                                        min_reels_count: Number(e.target.value) || undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                            />
                        </div>

                        <div className={styles.filter}>
                            <label htmlFor="maxReelsCount">Max reel creations</label>
                            <DebounceInput
                                placeholder="Unlimited"
                                id="maxReelsCount"
                                type="number"
                                debounceTimeout={500}
                                value={maxReelsCount}
                                onChange={(e) => {
                                    setUrlState({
                                        max_reels_count: Number(e.target.value) || undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.headerRight}>
                        <label>Sort by</label>
                        <Select
                            isSearchable={false}
                            isClearable={false}
                            styles={orderingSelectStyles}
                            value={selectedOrderingOption}
                            options={orderingOptions}
                            onChange={(option) => {
                                setUrlState({
                                    ordering: option?.value,
                                    page: 1,
                                });
                                reset();
                            }}
                        />
                    </div>
                </div>

                <InstagramAudioExploreTable
                    isLoading={isLoading}
                    data={rows}
                    count={count}
                    page={page}
                    pageSize={pageSize}
                    setPage={(page) => {
                        setUrlState({ page });
                        reset();
                    }}
                    setPageSize={(pageSize) => {
                        setUrlState({ page_size: pageSize });
                        reset();
                    }}
                    manualPagination
                    manualSorting
                    selectedOrdering={selectedOrderingOption?.value ?? DEFAULT_ORDERING}
                    onRowClick={(row) => {
                        setUrlState({ audio: row.original.audio_id });
                    }}
                />
            </div>
            <InstagramAudioModal
                isModalOpen={!!urlState.audio}
                closeModal={() => {
                    setUrlState({ audio: undefined });
                }}
                audioId={Number(urlState.audio)}
            />
        </>
    );
};

export default InstagramAudios;
